import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Flame, Plus, Check, Trash2 } from "lucide-react";

const COLORS = ["#E07A5F", "#81B29A", "#F2CC8F", "#2D3142", "#9B8AA6"];

export default function Habits() {
  const [habits, setHabits] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/habits"); setHabits(r.data); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const r = await api.post("/habits", { name: name.trim(), color });
    setHabits([...habits, r.data]);
    setName("");
    toast.success("Habit added");
  };

  const toggle = async (h) => {
    const r = await api.post(`/habits/${h.habit_id}/toggle`);
    setHabits(habits.map(x => x.habit_id === h.habit_id
      ? { ...x, completed_today: r.data.completed_today, streak: r.data.completed_today ? x.streak + (x.completed_today ? 0 : 1) : Math.max(0, x.streak - 1) }
      : x));
  };

  const del = async (h) => {
    await api.delete(`/habits/${h.habit_id}`);
    setHabits(habits.filter(x => x.habit_id !== h.habit_id));
  };

  return (
    <div className="space-y-8" data-testid="habits-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-[#8A8D98]">Daily practice</p>
        <h1 className="font-display text-4xl tracking-tight mt-2">Habits & streaks</h1>
        <p className="text-[#5b5f6e] mt-2 max-w-xl">Small quiet wins, stacked with care. Tap to mark today complete.</p>
      </div>

      <form onSubmit={add} className="soft-card p-5 flex flex-wrap items-center gap-3" data-testid="habit-create-form">
        <input
          value={name} onChange={e=>setName(e.target.value)}
          placeholder="e.g. Morning journaling · 10 min walk"
          className="flex-1 min-w-[200px] bg-transparent outline-none text-lg px-2 py-2 border-b border-[#E5E3DF] focus:border-[#E07A5F] transition-colors"
          data-testid="habit-name-input"
        />
        <div className="flex items-center gap-1">
          {COLORS.map(c => (
            <button type="button" key={c} onClick={()=>setColor(c)}
              className={`w-7 h-7 rounded-full transition-transform ${color===c ? "ring-2 ring-offset-2 ring-[#2D3142] scale-110" : "hover:scale-105"}`}
              style={{ background: c }} aria-label="choose color"
              data-testid={`color-${c}`}
            />
          ))}
        </div>
        <button type="submit" className="pill-btn bg-[#2D3142] text-white hover:bg-[#E07A5F]" data-testid="habit-create-btn">
          <Plus className="w-4 h-4 inline mr-1" /> Add habit
        </button>
      </form>

      {loading ? (
        <p className="text-[#8A8D98]">Loading…</p>
      ) : habits.length === 0 ? (
        <div className="soft-card p-10 text-center">
          <Flame className="w-8 h-8 mx-auto text-[#E07A5F]" />
          <p className="mt-3 font-display text-2xl">Your first ritual starts here.</p>
          <p className="text-[#8A8D98] mt-1">Add one small habit and show up tomorrow.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {habits.map(h => (
            <div key={h.habit_id} className="soft-card p-5 flex items-center gap-4" data-testid={`habit-${h.habit_id}`}>
              <button onClick={()=>toggle(h)}
                className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${h.completed_today ? "text-white" : "text-[#8A8D98]"}`}
                style={{ background: h.completed_today ? h.color : "#F5F2EC", border: `1px solid ${h.completed_today ? h.color : "#E5E3DF"}` }}
                data-testid={`toggle-${h.habit_id}`}
              >
                {h.completed_today ? <Check className="w-6 h-6" strokeWidth={3} /> : <span className="text-xl">◯</span>}
              </button>
              <div className="flex-1">
                <p className="font-medium text-lg">{h.name}</p>
                <p className="text-sm text-[#8A8D98]">
                  <Flame className="w-3.5 h-3.5 inline mr-1" style={{ color: h.color }} />
                  {h.streak} day streak
                </p>
              </div>
              <button onClick={()=>del(h)} className="text-[#8A8D98] hover:text-[#E63946] transition-colors" data-testid={`delete-${h.habit_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
